/**
 * IntakeEventStatusBadge — badge cohérent pour les 5 statuts intake_event
 * (Chunk 9.G.2 frontend, parité pattern CompanyStatusBadge + ProjectStatusBadge
 * + BrokerStatusBadge — décision PO Q6=A).
 *
 * Mapping `app.intake_event_status` (cf. supabase/migrations/0021) :
 *   - pending_classification → amber (à classer, action requise)
 *   - pending_resolution     → amber (à traiter, action requise)
 *   - resolved               → emerald (terminal positif)
 *   - rejected               → red (terminal négatif)
 *   - superseded             → slate (remplacé par un autre event)
 *
 * Pattern : dot + label, parité visuelle stricte avec autres status badges.
 */

import { useTranslation } from "react-i18next";

import type { IntakeEventBrokerSanitizedRow } from "@kollectivia/shared";

type Tone = "amber" | "emerald" | "red" | "slate";

const TONE: Record<Tone, { bg: string; text: string; dot: string }> = {
  amber: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
  red: { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" },
  slate: { bg: "bg-slate-100", text: "text-slate-700", dot: "bg-slate-400" },
};

type EventStatus = IntakeEventBrokerSanitizedRow["status"];

function toneFor(status: EventStatus): Tone {
  switch (status) {
    case "pending_classification":
    case "pending_resolution":
      return "amber";
    case "resolved":
      return "emerald";
    case "rejected":
      return "red";
    case "superseded":
      return "slate";
    default:
      return "slate";
  }
}

type Props = {
  readonly status: EventStatus;
};

export function IntakeEventStatusBadge({ status }: Props) {
  const { t } = useTranslation();
  const tone = TONE[toneFor(status)];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full ${tone.bg} px-2.5 py-0.5 text-xs font-medium ${tone.text}`}
    >
      <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
      {t(`intake_links.detail.events.filters.status_options.${status}`)}
    </span>
  );
}
